<template>
  <Page v-if="isReady">
    <BreadCrumb>
      <template #loanservice-banner v-if="showMovedToLoanServiceBanner">
        <div class="icon-circle-check loanservice-banner">
          This application has been completed and moved to Loan Service.
        </div>
      </template>
      <template #jumpto v-if="showJumpToMenu">
        <JumpToMenu :menuItems="menuItems"></JumpToMenu>
      </template>
    </BreadCrumb>
    <div>
      <div id="applicationDetails">
        <Details></Details>
      </div>
      <div id="collapseDetails">
        <ApplicationApplicantDetails />
      </div>
      <TabWrapper />
    </div>
  </Page>
  <Loader v-else></Loader>
</template>

<script setup lang="ts">
import Loader from "@/components/Loader.vue";
import BreadCrumb from "@/components/application/BreadCrumb.vue";
import Page from "@/components/layout/Page.vue";
import Details from "@/components/v2/application/Details.vue";
import ApplicationApplicantDetails from "@/components/v2/application/ApplicationApplicantDetails.vue";
import TabWrapper from "@/components/wrappers/TabWrapper.vue";
import { onMounted, onUnmounted, ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { useTypeStore } from "@/store/store/typeStore";
import { Type } from "@/types/types";
import { useStipulationStore } from "@/store/store/stipulationStore";
import JumpToMenu from "@/components/navigation/JumpToMenu.vue";
import { JumpToMenuLink } from "@/types/jumpto-menu";
import functions from "@/use/functions";

const route = useRoute();
const applicationStore = useApplicationStore();
const typeStore = useTypeStore();
const stipulationStore = useStipulationStore();
const isReady = ref(false);

const menuItems = computed<JumpToMenuLink[] | undefined>(
  () => applicationStore?.application?.detailViewModel?.jumpToMenuLinks
);
const showJumpToMenu = computed<boolean>(
  () => applicationStore?.application?.detailViewModel?.showJumpToMenu || false
);
const showMovedToLoanServiceBanner = computed<boolean>(
  () =>
    applicationStore?.application?.detailViewModel
      ?.showMovedToLoanServiceBanner || false
);

async function initializeApplication() {
	const referenceId = route.params.referenceId.toString();

  await Promise.all([
    applicationStore.getApplication(referenceId),
    applicationStore.getApplicationSummary(referenceId),
    applicationStore.getTabTags(referenceId),
    typeStore.getType(Type.JudgmentDenials),
    typeStore.getType(Type.ApplicationStatusReasons),
    typeStore.getDocServiceType(),
  ]);

  document.title = `${applicationStore.ApplicantSelected.lastName || ""},
		${applicationStore.ApplicantSelected.firstName || ""}.
		(${
      applicationStore.viewModel.referenceId?.trim() || ""
    }) - Loan Origination System`;

  isReady.value = true;
	const defaultTab = "ApplicationTab";
	
  let currentTab = localStorage.getItem(`${applicationStore.pageIdentifier}.currentTab`) || defaultTab;
  applicationStore.updateActiveTab(currentTab);

	functions.logPageView({
    id: referenceId,
    page: "applications",
    category: "Application",
    timestamp: Date.now(),
    firstName: applicationStore.application?.tabApplicationViewModel.primaryApplicant?.firstName,
    lastName: applicationStore.application?.tabApplicationViewModel.primaryApplicant?.lastName,
    name: applicationStore.application?.tabApplicationViewModel.primaryApplicant?.fullName,
    lenderId: applicationStore.application?.detailViewModel.lenderId,
		lenderName: applicationStore.application?.detailViewModel.lenderName
  });
}

onMounted(async () => {
	await initializeApplication();
});

onUnmounted(() => {
  applicationStore.$reset();
  stipulationStore.$reset();

  localStorage.removeItem("cache.selected");
  localStorage.removeItem(`${applicationStore.pageIdentifier}.currentTab`);
});

watch(
	() => route.params.referenceId,
	async (newReferenceId, oldReferenceId) => {
		if (newReferenceId !== oldReferenceId) {
			isReady.value = false;
			
			await initializeApplication();

			isReady.value = true;
		}
	}
);
</script>
<style scoped>
#grid > div {
  border: 1px solid red;
}
</style>
